import { Injectable } from '@angular/core';
import { createClient, Session, SupabaseClient } from '@supabase/supabase-js';
import { environment } from '@app/environment';
import { Observable, from, BehaviorSubject, throwError } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { RealtimePayload } from '@app/core/models/supabase.types';

@Injectable({
  providedIn: 'root'
})
export class SupabaseClientService {
  private supabase: SupabaseClient;
  private authStateSubject = new BehaviorSubject<Session | null>(null);

  constructor() {
    this.supabase = createClient(
      environment.supabase.url,
      environment.supabase.publicKey,
      {
        auth: {
          // Disable lock to prevent NavigatorLockAcquireTimeoutError
          lock: async <R>(name: string, acquireTimeout: number, fn: () => Promise<R>): Promise<R> => {
            // No-op lock - just execute the function without locking mechanism
            return await fn();
          }
        }
      }
    );

    // Initialize with current session
    this.supabase.auth.getSession().then(({ data: { session } }) => {
      this.authStateSubject.next(session);
    });

    // Listen to auth state changes
    this.supabase.auth.onAuthStateChange((event, session) => {
      this.authStateSubject.next(session);
    });
  }

  /**
   * Get Supabase client instance
   */
  get client(): SupabaseClient {
    return this.supabase;
  }

  /**
   * Reactive method for selecting data from any table
   */
  select<T>(table: string, columns: string = '*', filters?: any): Observable<T[]> {
    return from(
      (async () => {
        let query = this.supabase.from(table).select(columns);

        if (filters) {
          Object.keys(filters).forEach(key => {
            query = query.eq(key, filters[key]);
          });
        }

        const { data, error } = await query;

        if (error) throw error;
        return (data as T[]) || [];
      })()
    ).pipe(
      catchError(error => {
        console.error('Error selecting data:', error);
        return throwError(() => error);
      }),
      shareReplay(1)
    );
  }

  /**
   * Reactive method for inserting data into any table
   */
  insert<T>(table: string, data: Partial<T> | Partial<T>[]): Observable<T[]> {
    return from(
      this.supabase
        .from(table)
        .insert(data)
        .select()
    ).pipe(
      map(({ data: result, error }) => {
        if (error) throw error;
        return result || [];
      }),
      catchError(error => {
        console.error('Error inserting data:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Reactive method for updating data in any table
   */
  update<T>(table: string, data: Partial<T>, filters: any): Observable<T[]> {
    return from(
      (async () => {
        let query = this.supabase.from(table).update(data);

        Object.keys(filters).forEach(key => {
          query = query.eq(key, filters[key]);
        });

        const { data: result, error } = await query.select();

        if (error) throw error;
        return result || [];
      })()
    ).pipe(
      catchError(error => {
        console.error('Error updating data:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Reactive method for deleting data from any table
   */
  delete(table: string, filters: any): Observable<void> {
    return from(
      (async () => {
        let query = this.supabase.from(table).delete();

        Object.keys(filters).forEach(key => {
          query = query.eq(key, filters[key]);
        });

        const { error } = await query;

        if (error) throw error;
      })()
    ).pipe(
      catchError(error => {
        console.error('Error deleting data:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Reactive authentication methods
   */
  signUp(email: string, password: string): Observable<any> {
    return from(
      this.supabase.auth.signUp({ email, password })
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          throw error;
        }

        // Check if user was actually created or if it's an existing user
        if (data.user) {
          if (!data.user?.user_metadata?.['email']) {
            throw new Error('User with this email already exists. Please try logging in instead.');
          }

          return { ...data, isNewUser: true };
        }

        return data;
      }),
      catchError(error => {
        console.error('Error signing up:', error);
        return throwError(() => error);
      })
    );
  }

  signIn(email: string, password: string): Observable<any> {
    return from(
      this.supabase.auth.signInWithPassword({ email, password })
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          // Handle specific error cases
          if (error.message?.includes('Invalid login credentials')) {
            throw new Error('Invalid email or password. Please check your credentials and try again.');
          }
          if (error.message?.includes('Email not confirmed')) {
            throw new Error('Please check your email and click the confirmation link to activate your account before signing in.');
          }
          if (error.message?.includes('Too many requests')) {
            throw new Error('Too many login attempts. Please wait a moment before trying again.');
          }
          throw error;
        }
        return data;
      }),
      catchError(error => {
        console.error('Error signing in:', error);
        return throwError(() => error);
      })
    );
  }

  signOut(): Observable<void> {
    return from(
      this.supabase.auth.signOut()
    ).pipe(
      map(({ error }) => {
        if (error) throw error;
      }),
      catchError(error => {
        console.error('Error signing out:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get current user reactively
   */
  getCurrentUser(): Observable<any> {
    return from(this.supabase.auth.getUser()).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        if (!data.user) {
          throw new Error('User not authenticated');
        }
        return data.user;
      }),
      catchError(error => {
        console.error('Error getting current user:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Observable for auth state changes
   */
  get authState$(): Observable<Session | null> {
    return this.authStateSubject.asObservable();
  }

  /**
   * Realtime subscription for table changes
   */
  subscribeToTable(table: string, event: string = '*', filter?: string): Observable<any> {
    return new Observable(subscriber => {
      const subscription = this.supabase
        .channel(`${table}_changes`)
        .on('postgres_changes' as any,
          {
            event,
            schema: 'public',
            table,
            filter: filter ? `id=eq.${filter}` : undefined
          },
          (payload: any) => subscriber.next(payload)
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    });
  }

  /**
   * Realtime subscription for table changes with custom filter
   */
  subscribeToTableWithFilter(table: string, filterColumn: string, filterValue: string, event: string = '*'): Observable<RealtimePayload> {
    return new Observable<RealtimePayload>(subscriber => {
      const subscription = this.supabase
        .channel(`${table}_${filterColumn}_${filterValue}`)
        .on('postgres_changes' as any,
          {
            event,
            schema: 'public',
            table,
            filter: `${filterColumn}=eq.${filterValue}`
          },
          (payload: RealtimePayload) => subscriber.next(payload)
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    });
  }
}
